import { ColumnWithTranslation, Activity, EntryType } from '../../../models';
import { Mapper } from '../../../mappers';
import { Octopus } from '../..';
import { RecurrenceType } from '../models/recurrence-type';
import { Task as OctopusTask } from '../models/task';
import { ValueAndDisplay } from '../models';

export class ActivityManagerTaskMapper extends Mapper<
  OctopusTask[],
  Activity[]
> {
  private readonly MAX_SUBJECT_LENGTH = 200;
  constructor(private readonly columns?: ColumnWithTranslation[]) {
    super();
  }

  override from(source: OctopusTask[]): Activity[] {
    return source.map((item) => this.mapItem(item));
  }

  mapItem(source: OctopusTask): Activity {
    const activity: Activity = {
      key: source.Key,
      type: Octopus.InteractionCode.TASK,
      secStatus: source.SecStatus,
      completed: source.Completed ?? false,
      creator: source.Creator?.DisplayValue ?? '',
      subject: this.truncate(source.Activity ?? ''),
      users: source.AssignedTo?.DisplayValue ?? '',
      userKeys: source.AssignedTo?.Key ? [source.AssignedTo.Key] : [],
      category: this.getCategoryDisplay(source.Category),
      priority: source.Priority ?? '',
      completedBy: source.CompletedBy?.DisplayValue ?? '',
      completedByKeys: source.CompletedBy?.Key ? [source.CompletedBy.Key] : [],
      startDate: source.EffectiveDate?.Value ?? '',
      endDate: source.DateTime?.Value ?? '',
      completionDate: source.CompletionDate?.Value ?? '',
      abEntries: source.AbEntry
        ? [
            {
              Key: source.AbEntry.Key ?? '',
              DisplayValue: source.AbEntry.DisplayValue,
            },
          ]
        : [],
      leads: source.Lead
        ? [
            {
              Key: source.Lead.Key ?? '',
              DisplayValue: source.Lead.DisplayValue ?? '',
            },
          ]
        : [],
      recurrence: source.RecurrenceKey ?? null,
      isActionPlan: source.ActionPlanKey !== null,
      isWorkflowActivity:
        source.WorkflowInstanceKey !== null &&
        source.WorkflowInstanceKey !== '',
      isLastActivity:
        source.RecurrenceType !== null &&
        source.RecurrenceType === RecurrenceType.Master,
    };

    activity['entry'] =
      source.AbEntry?.DisplayValue ?? source.Lead?.DisplayValue ?? '';
    activity['c.entry'] = {
      type: this.entryTypeFor(source),
      clickable: !(activity.abEntries.length > 1 || activity.leads.length > 1),
    };

    this.setRegarding(activity, source);
    this.applyColumnMetadata(activity, source);

    return activity;
  }

  private getCategoryDisplay(
    category: string | ValueAndDisplay<string>,
  ): string {
    if (!category) {
      return '';
    }
    if (typeof category === 'string') {
      return category;
    }
    if (Array.isArray(category)) {
      return this.getCategoryDisplay(category[0]);
    }
    if (typeof category === 'object') {
      return category.DisplayValue ?? this.getCategoryDisplay(category.Value);
    }
    return '';
  }

  private truncate(value: string): string {
    return value.length > this.MAX_SUBJECT_LENGTH
      ? `${value.substring(0, this.MAX_SUBJECT_LENGTH)}...`
      : value;
  }

  private isEntryField(field: string): boolean {
    return field.includes('leads') || field.includes('abentries');
  }

  private resolveFieldId(column: ColumnWithTranslation): string {
    if (this.isEntryField(column.field.toLowerCase())) {
      return 'entry';
    }
    return column.field.replace('{{', '').replace('}}', '');
  }

  private setRegarding(activity: Activity, source: OctopusTask): void {
    if (source.With?.Opportunity) {
      activity.with = {
        id: source.With.Opportunity.Key,
        name: source.With.Opportunity[Octopus.OpportunityFields.Objective],
      };
    } else if (source.With?.Case) {
      activity.with = {
        id: source.With.Case.Key,
        name: source.With.Case[Octopus.CaseFields.Subject],
      };
    }
  }

  private applyColumnMetadata(activity: Activity, source: OctopusTask): void {
    if (!this.columns) {
      return;
    }

    const isMultipleEntries =
      activity.abEntries.length > 1 || activity.leads.length > 1;

    this.columns.forEach((column) => {
      const field = column.field.toLowerCase();
      const fieldId = this.resolveFieldId(column);

      this.applyColumnValue(activity, source, column, field, fieldId);

      if (
        !activity.secStatus.CanRead &&
        activity[fieldId] === '' &&
        !activity['private']
      ) {
        activity['private'] = { columnid: fieldId };
      }

      activity[`c.${fieldId}`] = {
        type: this.getEntryType(field, source),
        clickable: this.detectClickable(field, isMultipleEntries),
      };
    });
  }

  private applyColumnValue(
    activity: Activity,
    source: OctopusTask,
    column: ColumnWithTranslation,
    field: string,
    fieldId: string,
  ): void {
    const cell = this.getColumnCell(source, column.id);
    if (!cell) {
      return;
    }

    const value = (cell.Value ?? '').trim();
    if (
      field.includes('completiondate') ||
      field.includes('startdate') ||
      field.includes('enddate')
    ) {
      activity[`${fieldId}DisplayValue`] = value;
    } else if (
      !this.isMapperResolvedField(fieldId) ||
      (fieldId === 'category' && value)
    ) {
      activity[fieldId] = value;
    }
  }

  private isMapperResolvedField(fieldId: string): boolean {
    return (
      fieldId === 'entry' ||
      fieldId === 'with' ||
      fieldId === 'subject' ||
      fieldId === 'completed' ||
      fieldId === 'users' ||
      fieldId === 'completedBy' ||
      fieldId === 'creator' ||
      fieldId === 'category'
    );
  }

  private getColumnCell(
    source: OctopusTask,
    id: string,
  ): Octopus.ColumnItem | null {
    return (source as OctopusTask & Partial<Octopus.Columns>)[id] ?? null;
  }

  private entryTypeFor(source: OctopusTask): EntryType | '' {
    if (source.AbEntry) {
      return 'addressBook';
    }
    if (source.Lead) {
      return 'lead';
    }
    return '';
  }

  private getEntryType(field: string, source: OctopusTask): EntryType | '' {
    if (this.isEntryField(field)) {
      return this.entryTypeFor(source);
    }
    if (field.includes('with')) {
      if (source.With?.Opportunity) {
        return 'opportunity';
      }
      if (source.With?.Case) {
        return 'case';
      }
    }
    return '';
  }

  private detectClickable(field: string, isMultipleEntries: boolean): boolean {
    if (field.includes('subject')) {
      return true;
    }
    if (this.isEntryField(field)) {
      return !isMultipleEntries;
    }
    if (field.includes('with')) {
      return true;
    }
    return false;
  }
}
